/**
 * Reminders — the care schedule, its progress rings and its switches.
 *
 * WHAT THIS SCREEN IS NOT ALLOWED TO DO
 * ------------------------------------
 * It does not compute a due date, a days-left count, a status phrase or a cycle percentage. Every
 * one of those arrives on `CareItem` already derived by the server, which is the only reason Home's
 * "Coming up" and this screen cannot disagree. `fmtDay` appears exactly once here, to name the new
 * due date in a confirmation toast, and it reads a Date the API client already revived.
 *
 * THE THREE HONESTY PROBLEMS THIS SCREEN HAD TO SOLVE
 * --------------------------------------------------
 *  1. The prototype always drew seven rows with seven confident rings. Against the 199 live records
 *     the schedule is currently EMPTY for very nearly every dog, because the care-date fields were
 *     only added on 2026-07-30. So the interesting state here is the empty one, and it gets a
 *     designed panel and a "here is exactly what we need from you" card — not a blank card, and not
 *     a 0% bar, which reads as failure rather than as absence.
 *  2. `item.percent` is `number | null`, and null means "we hold the date but not the rhythm". A
 *     conic-gradient over null renders `NaN%`, which browsers quietly resolve to a full accent
 *     circle — a confident arc over data we do not have. Unknown gets a flat resting fill instead.
 *  3. The prototype's "Care readiness 82" was a design-tool knob (`props.careScore ?? 82`) with no
 *     field behind it. The panel keeps its geometry but now counts something real and says what it
 *     counted, so a client can check the number against the rows underneath it.
 *
 * Deliberate copy changes from the approved prototype, flagged for the owner rather than made
 * silently: the Delivery card's three values read "Coming soon" instead of "On" / "7 days before" /
 * "Only when overdue", and a note under the switches says the choices are not saved yet. Nothing
 * server-side sends a reminder today (the only mail the server sends is the sign-in link), and the
 * switches live in the UI reducer with no persistence. Shipping the original strings to a paying
 * client would be a promise the system cannot keep.
 */

import { useState, type CSSProperties, type ReactElement } from 'react'
import {
  CARE_ITEM_NAMES,
  fmtDay,
  type CareItem,
  type CareItemKind,
  type SelfAdministeredTreatment,
} from '@ft/shared'

import { Icon, careIconFor } from '../model/icons'
import { usePortal, useSelectedDog } from '../state/portal'

// ── What we ask for when it is missing ──────────────────────────────────────────────────────────

/**
 * The four items an owner can actually supply. `contact` is a schedule item too, but it is stamped
 * by the portal's own review flow — there is nothing for a client to bring us — so it never appears
 * in a "what we're missing" list.
 *
 * Written as `Extract<…>` rather than a bare union so that renaming a kind in @ft/shared collapses
 * this to `never` and fails the build here, instead of silently dropping a row from the card.
 */
type TrackedKind = Extract<CareItemKind, 'worming' | 'flea' | 'booster' | 'kennel'>

const TRACKED_KINDS: readonly TrackedKind[] = ['worming', 'flea', 'booster', 'kennel']

/**
 * Only the ASK text lives here. The display name comes from `CARE_ITEM_NAMES` in @ft/shared, so a
 * treatment renamed there renames here too — this used to hold its own copy of the four names,
 * connected to the real ones by nothing but a comment.
 */
const MISSING_COPY: Readonly<Record<TrackedKind, { readonly ask: string }>> = {
  worming: { ask: 'The date of the last worming treatment, and how often it is given.' },
  flea: { ask: 'The date of the last flea treatment, and how often it is given.' },
  booster: { ask: 'The date the annual booster is next due, from the vaccination card.' },
  kennel: { ask: 'The date the kennel cough vaccination is next due.' },
}

// ── Presentation helpers ────────────────────────────────────────────────────────────────────────

/**
 * The ring behind a care icon.
 *
 * Three states, not two. `percent === null` is a real answer — "Other / not sure" is a live option
 * on the registration form and produces a last-given date with no computable rhythm — so it gets a
 * flat accent tint that says "this is on, we just cannot draw progress". Interpolating null into
 * the gradient would render `NaN%`, which browsers resolve to a full circle: the most confident
 * possible drawing of the least certain data.
 */
function ringBackground(on: boolean, percent: number | null): string {
  if (!on) return 'var(--color-neutral-200)'
  if (percent === null) return 'var(--color-accent-100)'
  return `conic-gradient(var(--color-accent) ${percent}%, var(--color-accent-200) 0)`
}

/**
 * Narrow a care kind to the two treatments the write endpoint accepts.
 *
 * A ternary rather than a cast: `MarkGivenRequest.treatment` excludes booster and kennel cough on
 * purpose — they are vet-issued compliance artefacts the business's insurance depends on — and a
 * cast here would be the one line that quietly re-opens them to one-tap self-certification.
 */
function asTreatment(kind: CareItemKind): SelfAdministeredTreatment | null {
  return kind === 'worming' || kind === 'flea' ? kind : null
}

// ── Styles (lifted from the prototype, dc.html:214-277) ─────────────────────────────────────────
//
// Every value is a STRING. React appends `px` to a bare number, and the prototype's own converter
// kept values as trimmed strings — `width: 8` here would silently mean `8px` where the design said
// nothing of the sort.

const SCREEN: CSSProperties = { animation: 'ft-rise .4s ease both' }
const HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '14px',
  marginBottom: '20px',
}
const KICKER: CSSProperties = {
  fontSize: '12px',
  fontWeight: '700',
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-700)',
  marginBottom: '7px',
}
const H1: CSSProperties = { margin: '0 0 7px', fontSize: 'clamp(30px, 5.5vw, 42px)' }
const LEAD: CSSProperties = { margin: '0', fontSize: '15px' }

const PANEL: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--color-accent-2-800)',
  color: 'var(--color-bg)',
  borderRadius: 'calc(var(--radius-lg) * 1.15)',
  padding: '22px',
  boxShadow: 'var(--shadow-md)',
  marginBottom: '18px',
}
const PANEL_BLOB: CSSProperties = {
  position: 'absolute',
  width: '220px',
  height: '220px',
  borderRadius: '50%',
  background: 'var(--color-accent-2-700)',
  opacity: '.5',
  right: '-70px',
  top: '-120px',
}
const PANEL_ROW: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: '14px',
}
const PANEL_KICKER: CSSProperties = {
  fontSize: '11px',
  letterSpacing: '.1em',
  textTransform: 'uppercase',
  fontWeight: '600',
  color: 'var(--color-accent-2-300)',
}
const PANEL_FIGURE: CSSProperties = { fontFamily: 'var(--font-heading)', fontSize: '54px', lineHeight: '1.05' }
const PANEL_WORD: CSSProperties = { fontSize: '16px' }
const PANEL_SUB: CSSProperties = { fontSize: '12px', color: 'var(--color-accent-2-200)', marginTop: '3px' }
const PANEL_TRACK: CSSProperties = {
  position: 'relative',
  height: '8px',
  background: 'var(--color-accent-2-700)',
  borderRadius: '999px',
  overflow: 'hidden',
  marginTop: '16px',
}
const PANEL_BODY: CSSProperties = { position: 'relative' }
const PANEL_EMPTY_TITLE: CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: 'clamp(24px, 4.5vw, 30px)',
  lineHeight: '1.15',
  marginTop: '6px',
}
const PANEL_EMPTY_TEXT: CSSProperties = {
  margin: '9px 0 0',
  fontSize: '13px',
  lineHeight: '1.55',
  color: 'var(--color-accent-2-200)',
  maxWidth: '46ch',
}

const LIST_CARD: CSSProperties = { gap: '0', padding: '4px 0', marginBottom: '18px' }
const LIST: CSSProperties = { listStyle: 'none', margin: '0', padding: '0' }
const RING_INNER: CSSProperties = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  background: 'var(--color-surface)',
  display: 'grid',
  placeItems: 'center',
}
const MIDDLE: CSSProperties = { minWidth: '0' }
const ROW_NAME: CSSProperties = { display: 'block', fontWeight: '700', fontSize: '14.5px' }
const ROW_DETAIL: CSSProperties = { display: 'block', fontSize: '12px', marginTop: '2px' }
const ROW_TAG: CSSProperties = { marginTop: '7px', fontWeight: '700' }
const ROW_ASK: CSSProperties = {
  display: 'block',
  fontSize: '12px',
  lineHeight: '1.5',
  marginTop: '7px',
  maxWidth: '40ch',
}
const ACTIONS: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '10px',
  flexWrap: 'wrap',
}
// 38px, below the 44px floor the rest of the portal holds to. That is the approved design's own
// measurement — it sits inside a 76px-tall row, so the row itself is the comfortable target.
const MARK_BTN: CSSProperties = { minHeight: '38px', fontSize: '13px' }
const SWITCH: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  border: '0',
  background: 'transparent',
  cursor: 'pointer',
  padding: '7px 0',
  font: 'inherit',
}
const SWITCH_KNOB: CSSProperties = {
  display: 'block',
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: 'var(--color-neutral-100)',
  boxShadow: 'var(--shadow-sm)',
  transition: 'transform .2s',
}

const SECTION_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px',
  margin: '0 4px 12px',
}
const SECTION_H3: CSSProperties = { margin: '0', fontSize: '22px' }
const SECTION_SUB: CSSProperties = { fontSize: '12.5px' }

const DELIVERY_CARD: CSSProperties = { gap: '0', padding: '8px 20px' }
const DELIVERY_ROW: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40px 1fr auto',
  gap: '12px',
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid var(--color-divider)',
}
const DELIVERY_ROW_LAST: CSSProperties = { ...DELIVERY_ROW, borderBottom: 'none' }
const DELIVERY_DISC: CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--color-accent-2-100)',
  color: 'var(--color-accent-2-700)',
}
const DELIVERY_LABEL: CSSProperties = { fontSize: '14px', fontWeight: '600' }
const DELIVERY_VALUE: CSSProperties = { fontSize: '13px', fontWeight: '600' }

const NOTE: CSSProperties = { margin: '0 4px 18px', fontSize: '12.5px', lineHeight: '1.55' }

const PLAIN_CARD: CSSProperties = { gap: '0', padding: '24px', marginBottom: '18px' }
const CARD_KICKER: CSSProperties = { fontSize: '11px' }
const CARD_TITLE: CSSProperties = { margin: '8px 0 6px', fontSize: 'clamp(21px, 3vw, 25px)' }
const CARD_LEAD: CSSProperties = { margin: '0', fontSize: '14px', lineHeight: '1.55' }
const MISS_LIST: CSSProperties = { listStyle: 'none', margin: '18px 0 0', padding: '0', display: 'grid', gap: '14px' }
const MISS_ITEM: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  gap: '12px',
  alignItems: 'start',
}
const MISS_DISC: CSSProperties = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  background: 'var(--color-accent-100)',
  color: 'var(--color-accent-700)',
}
const MISS_NAME: CSSProperties = { display: 'block', fontSize: '14px', fontWeight: '700' }
const MISS_ASK: CSSProperties = { display: 'block', fontSize: '12.5px', lineHeight: '1.5', marginTop: '2px' }
const RETRY_ROW: CSSProperties = { display: 'flex', marginTop: '16px' }
const RETRY_BTN: CSSProperties = { minHeight: '44px' }

/**
 * Visually hidden, still announced. Written inline rather than as a `.sr-only` class because
 * neither the design system nor portal.css defines one, and a class name that resolves to no rule
 * is the worst of both worlds — the text is simply visible, and nothing reports it.
 */
const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: '0',
}

// ── The screen ──────────────────────────────────────────────────────────────────────────────────

export function RemindersScreen(): ReactElement {
  const { me } = usePortal()
  const dog = useSelectedDog()

  return (
    <section data-screen-label="Reminders" style={SCREEN}>
      <header style={HEADER}>
        <div>
          <div style={KICKER}>Personalised care</div>
          <h1 style={H1}>Reminders</h1>
          <p className="text-muted" style={LEAD}>
            Choose what matters and we&rsquo;ll keep an eye on the dates.
          </p>
        </div>
      </header>

      {/* The first load is the only state with nothing to show. A refresh behind existing data
          deliberately keeps the schedule on screen rather than replacing it with a skeleton. */}
      {me.status === 'loading' ? (
        <section className="card elev-sm" style={PLAIN_CARD} aria-busy="true">
          <p className="text-muted" style={CARD_LEAD}>
            Loading your reminders&hellip;
          </p>
        </section>
      ) : null}

      {/* A failed first load must never render as "nothing is due" — that is the same sentence a
          perfectly healthy record with no dates would produce, and the client cannot tell them
          apart. Say the load failed, and offer the retry. */}
      {me.status === 'error' ? (
        <section className="card elev-sm" style={PLAIN_CARD}>
          <div className="card-kicker" style={CARD_KICKER}>
            Reminders
          </div>
          <h2 style={CARD_TITLE}>We couldn&rsquo;t load your reminders</h2>
          <p className="text-muted" style={CARD_LEAD}>
            {me.error ? me.error.message : 'We couldn’t reach the portal just now.'}
          </p>
          <div style={RETRY_ROW}>
            <button type="button" className="btn btn-secondary" style={RETRY_BTN} onClick={me.refresh}>
              Try again
            </button>
          </div>
        </section>
      ) : null}

      {me.status === 'ready' && !dog ? (
        <section className="card elev-sm" style={PLAIN_CARD}>
          <div className="card-kicker" style={CARD_KICKER}>
            Reminders
          </div>
          <h2 style={CARD_TITLE}>There&rsquo;s no dog on your record yet</h2>
          <p className="text-muted" style={CARD_LEAD}>
            As soon as your dog is added, their worming, flea and vaccination dates appear here and
            we start counting down to each one.
          </p>
        </section>
      ) : null}

      {dog && me.data ? <Schedule dog={dog} serverDate={me.data.serverDate} /> : null}

      <div style={SECTION_HEAD}>
        <div>
          <h3 style={SECTION_H3}>Delivery</h3>
          <span className="text-muted" style={SECTION_SUB}>
            How nudges will reach you
          </span>
        </div>
      </div>
      <DeliveryCard />
    </section>
  )
}

// ── The schedule ────────────────────────────────────────────────────────────────────────────────

interface ScheduleProps {
  readonly dog: { readonly dogId: string; readonly name: string; readonly care: readonly CareItem[] }
  /** yyyy-mm-dd from the server. The client's own clock is never the source of a recorded date. */
  readonly serverDate: string
}

function Schedule({ dog, serverDate }: ScheduleProps): ReactElement {
  const items = dog.care

  // `feeding` is a daily routine with no "next", so it is not something that can be on or off
  // track — counting it would inflate the readiness figure with a row that can never be overdue.
  const scheduled = items.filter((item) => item.kind !== 'feeding')
  const attention = scheduled.filter((item) => item.urgency === 'attention').length
  const onTrack = scheduled.length - attention
  const missing = TRACKED_KINDS.filter((kind) => !items.some((item) => item.kind === kind))

  return (
    <>
      {scheduled.length > 0 ? (
        <ReadinessPanel onTrack={onTrack} tracked={scheduled.length} attention={attention} />
      ) : (
        <EmptyPanel dogName={dog.name} />
      )}

      {items.length > 0 ? (
        <div className="card elev-sm" style={LIST_CARD}>
          <ul style={LIST}>
            {items.map((item) => (
              <ReminderRow
                key={item.kind}
                item={item}
                dogId={dog.dogId}
                dogName={dog.name}
                serverDate={serverDate}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {items.length > 0 ? (
        <p className="text-muted" style={NOTE}>
          Reminders aren&rsquo;t switched on yet, so these choices aren&rsquo;t saved. Tell us what
          you&rsquo;d like at your next visit and we&rsquo;ll set them up for you.
        </p>
      ) : null}

      {missing.length > 0 ? (
        <MissingCard dogName={dog.name} missing={missing} hasSchedule={scheduled.length > 0} />
      ) : null}
    </>
  )
}

// ── Care readiness ──────────────────────────────────────────────────────────────────────────────

interface ReadinessPanelProps {
  readonly onTrack: number
  readonly tracked: number
  readonly attention: number
}

/**
 * The prototype's score panel, keeping its geometry and losing its invented number.
 *
 * "82" came from a design-tool prop with no field behind it. What is shown instead is a count a
 * client can verify against the rows immediately below — which is the only kind of number worth
 * putting at 54px.
 */
function ReadinessPanel({ onTrack, tracked, attention }: ReadinessPanelProps): ReactElement {
  const word = attention === 0 ? 'All on track' : attention === tracked ? 'Needs attention' : 'Mostly on track'
  const sub =
    attention === 0
      ? 'Everything we hold a date for is up to date'
      : `${attention} ${attention === 1 ? 'thing needs' : 'things need'} attention`
  const fill: CSSProperties = {
    display: 'block',
    height: '100%',
    width: `${Math.round((onTrack / tracked) * 100)}%`,
    background: 'var(--color-accent-2-300)',
    borderRadius: '999px',
    transition: 'width .4s ease',
  }

  return (
    <section style={PANEL}>
      <span aria-hidden="true" style={PANEL_BLOB} />
      <div style={PANEL_ROW}>
        <div>
          <div style={PANEL_KICKER}>Care readiness</div>
          {/* Read out as "3 of 4" rather than "3 slash 4" — the slash is a visual shorthand and
              a screen reader has no reason to guess what it means. */}
          <div style={PANEL_FIGURE} aria-hidden="true">
            {onTrack}/{tracked}
          </div>
          <span style={SR_ONLY}>{`${onTrack} of ${tracked} on track`}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <strong style={PANEL_WORD}>{word}</strong>
          <div style={PANEL_SUB}>{sub}</div>
        </div>
      </div>
      <div style={PANEL_TRACK}>
        <span style={fill} />
      </div>
    </section>
  )
}

/**
 * The same panel when there is no schedule at all — which is where nearly every real record starts.
 *
 * No figure and no bar. A "0" at 54px and an empty progress track read as a failing grade for the
 * owner; the truth is that we have not been given the dates yet, and that is our sentence to write,
 * not theirs to infer.
 */
function EmptyPanel({ dogName }: { readonly dogName: string }): ReactElement {
  return (
    <section style={PANEL}>
      <span aria-hidden="true" style={PANEL_BLOB} />
      <div style={PANEL_BODY}>
        <div style={PANEL_KICKER}>Care readiness</div>
        <div style={PANEL_EMPTY_TITLE}>Nothing to count down to yet</div>
        <p style={PANEL_EMPTY_TEXT}>
          Once we have {dogName}&rsquo;s treatment and vaccination dates, this is where you&rsquo;ll
          see what&rsquo;s due, when, and how much of the cycle has gone.
        </p>
      </div>
    </section>
  )
}

// ── One care row ────────────────────────────────────────────────────────────────────────────────

interface ReminderRowProps {
  readonly item: CareItem
  readonly dogId: string
  readonly dogName: string
  readonly serverDate: string
}

function ReminderRow({ item, dogId, dogName, serverDate }: ReminderRowProps): ReactElement {
  const { state, toggleReminder, showToast, markGiven } = usePortal()
  const [pending, setPending] = useState(false)

  // `ToggleKey` IS `CareItemKind`, so this lookup cannot miss. The prototype keyed the switches off
  // a separately maintained list, and a miss rendered `aria-checked="undefined"` — an invalid ARIA
  // value that assistive tech treats as neither checked nor unchecked.
  const treatment = asTreatment(item.kind)

  /**
   * A reminder switch only makes sense for something with a NEXT. Feeding is a standing daily
   * routine, so there is no date to be nudged before — it carries no switch at all.
   *
   * This started as `INITIAL_TOGGLES.feeding = false`, whose reasoning was right (a daily nudge
   * would be noise) but whose effect was wrong: the row rendered dimmed with an OFF switch, which
   * reads as "you turned this off" for the one row many dogs will actually have. An absent control
   * says "there is nothing to schedule here"; an off control says "you disabled it".
   */
  const schedulable = item.kind !== 'feeding'
  const on = schedulable ? state.toggles[item.kind] : true

  const row: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '52px 1fr auto',
    gap: '14px',
    alignItems: 'center',
    padding: '13px 16px',
    borderBottom: '1px solid var(--color-divider)',
    opacity: on ? '1' : '.55',
    transition: 'opacity .2s',
  }
  const ring: CSSProperties = {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: ringBackground(on, item.percent),
  }
  const ringInner: CSSProperties = {
    ...RING_INNER,
    color: on ? 'var(--color-accent-800)' : 'var(--color-neutral-600)',
  }
  const switchLabel: CSSProperties = {
    fontSize: '11.5px',
    fontWeight: '700',
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: on ? 'var(--color-accent-800)' : 'var(--color-neutral-600)',
  }
  const switchTrack: CSSProperties = {
    display: 'block',
    width: '50px',
    height: '30px',
    flex: 'none',
    borderRadius: '999px',
    padding: '3px',
    background: on ? 'var(--color-accent)' : 'var(--color-neutral-400)',
    transition: 'background .2s',
  }
  const knob: CSSProperties = { ...SWITCH_KNOB, transform: on ? 'translateX(20px)' : 'translateX(0)' }

  async function onMark(): Promise<void> {
    // Re-entry guard rather than `disabled`: disabling the focused button drops a keyboard user
    // back to the top of the document mid-task, and the request usually resolves in a blink.
    if (!treatment || pending) return
    setPending(true)
    // `serverDate`, never `new Date()`. A device with a wrong clock would otherwise post a date the
    // server has to reject as "in the future", and the client would see a failure it cannot explain.
    const result = await markGiven({ dogId, treatment, givenOn: serverDate })
    setPending(false)

    if (!result.ok) {
      showToast(`We couldn’t record that — ${result.error.message}`)
      return
    }
    // The new due date comes from the SERVER's recomputed schedule, which `markGiven` has already
    // spliced into the cached record. Nothing here works out a next date locally.
    const updated = result.data.care.find((entry) => entry.kind === item.kind)
    showToast(
      updated?.due
        ? `${item.name} marked as given · next due ${fmtDay(updated.due)}`
        : `${item.name} marked as given`,
    )
  }

  return (
    <li style={row}>
      <span style={ring}>
        <span style={ringInner}>
          <Icon paths={careIconFor(item.kind)} size={17} />
        </span>
      </span>

      {/* min-width:0 is the guard that stops a long detail line blowing out the 1fr track. */}
      <span style={MIDDLE}>
        <span style={ROW_NAME}>{item.name}</span>
        {/* Empty for an item whose interval never parsed — an empty span here would render as a
            stray blank line rather than as nothing. */}
        {item.detail ? (
          <span className="text-muted" style={ROW_DETAIL}>
            {item.detail}
          </span>
        ) : null}
        {item.hasStatus ? (
          <span className={item.urgency === 'attention' ? 'tag tag-accent' : 'tag tag-accent-2'} style={ROW_TAG}>
            {item.status}
          </span>
        ) : null}
        {/* We hold the date but not the rhythm, so the ring is blank and no countdown is possible.
            Ask for the missing half rather than leaving the row looking broken. */}
        {item.needsInterval ? (
          <span className="text-muted" style={ROW_ASK}>
            We have the date but not how often. Tell us at your next visit and we&rsquo;ll start the
            countdown for {dogName}.
          </span>
        ) : null}
      </span>

      <span style={ACTIONS}>
        {/* Worming and flea only. Booster and kennel cough are vet-issued compliance artefacts —
            `MarkGivenRequest` cannot even express them, and `asTreatment` returns null for them. */}
        {item.selfAdministered && treatment ? (
          <button
            type="button"
            className="btn btn-ghost"
            style={MARK_BTN}
            aria-busy={pending ? 'true' : 'false'}
            onClick={() => void onMark()}
          >
            {pending ? 'Saving…' : 'Mark as given'}
          </button>
        ) : null}

        {schedulable ? (
        <button
          type="button"
          role="switch"
          // Stringified at the ARIA boundary on purpose: React omits an attribute whose value is a
          // literal `false`, and a missing `aria-checked` on a switch announces as nothing at all.
          aria-checked={on ? 'true' : 'false'}
          // No verb in the name. `role="switch"` plus `aria-checked` already announces the state,
          // so "Pause worming reminders" would contradict itself the moment it is switched off.
          aria-label={`${item.name} reminders`}
          style={SWITCH}
          onClick={() => {
            // `on` is the value captured at render — the PRE-click state. Reading it back after the
            // dispatch inverts both messages, which is exactly the bug the prototype avoided here.
            toggleReminder(item.kind)
            showToast(on ? `${item.name} reminders paused` : `${item.name} reminders on`)
          }}
        >
          {/* The visible ON/OFF text is what survives forced-colors mode, where the accent fill and
              the knob position both collapse into the system palette and stop meaning anything.
              Hidden from assistive tech because aria-checked has already said it. */}
          <span aria-hidden="true" style={switchLabel}>
            {on ? 'On' : 'Off'}
          </span>
          <span style={switchTrack}>
            <span style={knob} />
          </span>
        </button>
        ) : null}
      </span>
    </li>
  )
}

// ── What we still need ──────────────────────────────────────────────────────────────────────────

interface MissingCardProps {
  readonly dogName: string
  readonly missing: readonly TrackedKind[]
  readonly hasSchedule: boolean
}

/**
 * The designed absence.
 *
 * Two registers from one component: a household with nothing on file needs to be told what this
 * screen is for, while a household with three of four dates needs a short, unembarrassing nudge
 * about the fourth. Both list the same thing — precisely what to bring — because "your record is
 * incomplete" without the specifics is just an accusation.
 */
function MissingCard({ dogName, missing, hasSchedule }: MissingCardProps): ReactElement {
  return (
    <section className="card elev-sm" style={PLAIN_CARD}>
      <div className="card-kicker" style={CARD_KICKER}>
        {hasSchedule ? 'Still to come' : 'What we need'}
      </div>
      <h2 style={CARD_TITLE}>
        {hasSchedule
          ? `A few of ${dogName}’s dates are missing`
          : `We don’t have ${dogName}’s care dates yet`}
      </h2>
      <p className="text-muted" style={CARD_LEAD}>
        {hasSchedule
          ? 'Everything above is covered. These aren’t on the record yet, so nothing is counting down for them.'
          : 'Reminders start the moment we have them. Bring them to your next visit — a photo of the vaccination card or the treatment packet is plenty.'}
      </p>
      <ul style={MISS_LIST}>
        {missing.map((kind) => (
          <li key={kind} style={MISS_ITEM}>
            <span style={MISS_DISC}>
              <Icon paths={careIconFor(kind)} size={16} />
            </span>
            <span>
              <span style={MISS_NAME}>{CARE_ITEM_NAMES[kind]}</span>
              <span className="text-muted" style={MISS_ASK}>
                {MISSING_COPY[kind].ask}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

// ── Delivery ────────────────────────────────────────────────────────────────────────────────────

/**
 * How a nudge would reach someone — once there is something to send it with.
 *
 * The prototype's values ("On", "7 days before", "Only when overdue") described a delivery system
 * that does not exist: the server sends exactly one kind of mail, the sign-in link. The card keeps
 * its shape and states the truth instead. Change these three strings the day a sender ships.
 *
 * The glyphs are inlined rather than routed through `<Icon>` because two of the three mix `<rect>`
 * with `<path>`, which `<Icon>` cannot draw — and splitting a trio of matching icons across two
 * mechanisms is a worse trap than repeating the wrapper attributes three times.
 */
function DeliveryCard(): ReactElement {
  return (
    <section className="card elev-sm" style={DELIVERY_CARD}>
      <div style={DELIVERY_ROW}>
        <span style={DELIVERY_DISC}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
            <path d="M12 18h.01" />
          </svg>
        </span>
        <span style={DELIVERY_LABEL}>Push notification</span>
        <span className="text-muted" style={DELIVERY_VALUE}>
          Coming soon
        </span>
      </div>

      <div style={DELIVERY_ROW}>
        <span style={DELIVERY_DISC}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </span>
        <span style={DELIVERY_LABEL}>Email</span>
        <span className="text-muted" style={DELIVERY_VALUE}>
          Coming soon
        </span>
      </div>

      <div style={DELIVERY_ROW_LAST}>
        <span style={DELIVERY_DISC}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
        <span style={DELIVERY_LABEL}>Text message</span>
        <span className="text-muted" style={DELIVERY_VALUE}>
          Coming soon
        </span>
      </div>
    </section>
  )
}
